import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gamesApi, betsApi, communityApi, Bet, Game, Settlement, CommunityUser } from '../api';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft, Users, Calendar, Play, Plus,
  CheckCircle, DollarSign, Target, Clock, Share2, X, Search, UserPlus
} from 'lucide-react';
import Layout from '../components/Layout';
import EmptyState from '../components/EmptyState';
import Avatar from '../components/Avatar';
import { formatSAST, parseSAST } from '../utils/datetime';
import { formatHandicapIndex } from '../utils/handicapIndex';
import { blowUpCommentary } from '../utils/scoreCommentary';

interface Player {
  id: number;
  player_id: number;
  name: string;
  handicap?: number;
  has_accepted: boolean;
  has_submitted_scores?: boolean;
  has_verified_results?: boolean;
  total_strokes?: number;
  final_position?: number;
}

interface PlayerScoreLine {
  player_id: number;
  player_name: string;
  handicap?: number;
  total_strokes: number;
  net_strokes?: number;
  holes_played: number;
  scores: { hole: number; strokes: number; net?: number }[];
}

interface GameDetail extends Game {
  players: Player[];
  scores?: PlayerScoreLine[];
  hole_pars?: number[];
  final_scores_submitted?: boolean;
  all_scores_verified?: boolean;
  payout_released?: boolean;
}

const GameHub: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [games, setGames] = useState<Game[]>([]);
  const [pendingInvites, setPendingInvites] = useState<GameDetail[]>([]);
  const [gameQuery, setGameQuery] = useState('');
  const [gameFilter, setGameFilter] = useState<'all' | 'live' | 'upcoming' | 'finished'>('all');
  const [selectedGame, setSelectedGame] = useState<GameDetail | null>(null);
  const [bets, setBets] = useState<Bet[]>([]);
  const [joinRequests, setJoinRequests] = useState<{ id: number; user_id: number; user_name: string; handicap?: number; created_at: string }[]>([]);
  const [activeTab, setActiveTab] = useState<'list' | 'details' | 'betting' | 'scoring'>('list');
  const [isLoading, setIsLoading] = useState(true);
  type BetKind = 'winner_takes_all' | 'per_hole' | 'single_hole' | 'side_bet';
  const readLast = (key: string, fallback: string) => {
    try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
  };
  const [betAmount, setBetAmount] = useState(() => Number(readLast('fp_last_stake', '200')) || 200);
  const [customBet, setCustomBet] = useState('');
  const [betError, setBetError] = useState('');
  const [betType, setBetType] = useState<BetKind>(
    () => (readLast('fp_last_bettype', 'winner_takes_all') as BetKind)
  );
  const [betHole, setBetHole] = useState<number>(1);
  const [sideOnHole, setSideOnHole] = useState(false);
  const [sideLabel, setSideLabel] = useState('Longest Drive');
  const [customSide, setCustomSide] = useState('');
  const [betDrink, setBetDrink] = useState('');
  const [focusHole, setFocusHole] = useState<number | null>(null);
  const [scoringSheet, setScoringSheet] = useState<'bets' | 'card' | 'leaderboard' | null>(null);
  const [featuredBetId, setFeaturedBetId] = useState<number | null>(null);
  // Optimistic local scorecard for the current player, keyed by hole.
  const [myCard, setMyCard] = useState<Record<number, number>>({});
  const refreshInFlight = useRef(false);
  const refreshQueued = useRef(false);
  const [verifications, setVerifications] = useState<any[]>([]);
  const [settleResult, setSettleResult] = useState<any | null>(null);
  const [settlements, setSettlements] = useState<Record<number, Settlement | null>>({});
  const [actionMsg, setActionMsg] = useState('');
  const [busyBet, setBusyBet] = useState<number | null>(null);
  const [showBetSetup, setShowBetSetup] = useState(false);
  const [showHandicapConfirmation, setShowHandicapConfirmation] = useState(false);
  const [handicapsConfirmed, setHandicapsConfirmed] = useState(false);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [playerQuery, setPlayerQuery] = useState('');
  const [playerResults, setPlayerResults] = useState<CommunityUser[]>([]);
  const [contactInput, setContactInput] = useState('');
  const [rosterBusy, setRosterBusy] = useState(false);
  const [swipedPlayerId, setSwipedPlayerId] = useState<number | null>(null);
  const [swipedBetId, setSwipedBetId] = useState<number | null>(null);
  const [swipedGameId, setSwipedGameId] = useState<number | null>(null);
  const swipeStartX = useRef<number | null>(null);

  // A mix of classics and cheeky ones. Some suit a specific hole (longest
  // drive, nearest the pin), others are whole-round (most 3-putts, etc.).
  const SIDE_PRESETS = [
    'Longest Drive',
    'Nearest the Pin',
    'Longest Putt',
    'Worst Shot 🪵',
    'First in the Water 💦',
    'Most 3-Putts',
    'Sandbagger of the Day',
    'Beers on the Loser 🍺',
  ];
  // These default to being tied to a specific hole.
  const HOLE_SIDE_BETS = new Set(['Longest Drive', 'Nearest the Pin']);
  const DRINK_OPTIONS = ['Rum', 'Tequila', 'Jägermeister', 'Whisky', 'Brandy', 'Gin', 'Espresso shot', 'Dealer’s choice'];

  const holeCount = selectedGame?.holes ?? 18;
  const HOLES = Array.from({ length: holeCount }, (_, i) => i + 1);
  const holePars = selectedGame?.hole_pars ?? [];
  const parForHole = (h: number) => holePars[h - 1] ?? 4;
  const diffLabel = (d: number, hole?: number, strokes?: number) =>
    d <= -3 ? 'Albatross' : d === -2 ? 'Eagle' : d === -1 ? 'Birdie' : d === 0 ? 'Par' : d === 1 ? 'Bogey' : d === 2 ? 'Double bogey' : blowUpCommentary(selectedGame?.course_name || 'the course', hole || 1, strokes || d);
  const scoreLabel = (strokes: number, par: number, hole: number) => {
    if (strokes === 1) return 'Hole in one!';
    const difference = strokes - par;
    if (difference <= -3) return 'Albatross';
    if (difference === -2) return 'Eagle';
    if (difference === -1) return 'Birdie';
    if (difference === 0) return 'Par';
    if (difference === 1) return 'Bogey';
    if (difference === 2) return 'Double bogey';
    return blowUpCommentary(selectedGame?.course_name || 'the course', hole, strokes);
  };
  const diffClass = (d: number) =>
    d < 0 ? 'text-emerald-600' : d === 0 ? 'text-gray-700' : d === 1 ? 'text-amber-600' : 'text-red-600';
  const isCreator = !!(selectedGame && user && selectedGame.created_by === user.id);
  // The game-detail payload may not carry player_count on every path; fall back
  // to the loaded players list so "N/4 players" never renders blank.
  const playerCount = selectedGame?.player_count ?? selectedGame?.players?.length ?? 0;
  const acceptedCount = selectedGame?.players?.filter((p) => p.has_accepted).length ?? 0;
  const myPlayer = selectedGame?.players?.find((p) => p.id === user?.id);
  // The main match stake (auto-paid on accept) and any optional side bets.
  const mainMatchBet = bets.find((b) => ['winner_takes_all', 'per_hole'].includes(b.bet_type) && b.status !== 'settled');
  const openSideBets = bets.filter((b) => ['side_bet', 'single_hole'].includes(b.bet_type) && b.status !== 'settled');
  const hasMatchedBet = bets.some((bet) => bet.status !== 'cancelled' && bet.status !== 'settled' && bet.participant_count >= 2 && bet.total_pool >= bet.amount_per_player * 2);
  const myScores: Record<number, number> = {};
  selectedGame?.scores?.find((s: any) => s.player_id === user?.id)?.scores?.forEach((sc: any) => {
    myScores[sc.hole] = sc.strokes;
  });

  useEffect(() => {
    loadData();
    setMyCard({}); // clear optimistic scores when switching games
  }, [id]);

  // Keep the game lobby current. Direct invites are already present in the real
  // game list; hydrate pending games just enough to identify the current player's
  // acceptance state and show a prominent banner without changing the API.
  useEffect(() => {
    if (id || !user) return;
    let inFlight = false;
    const syncLobby = async () => {
      if (document.visibilityState !== 'visible' || inFlight) return;
      inFlight = true;
      try {
        const gameList = await gamesApi.getMyGames();
        const safeGames = Array.isArray(gameList) ? gameList : [];
        setGames(safeGames);
        const candidates = safeGames.filter((game) => game.status === 'pending' && game.created_by !== user.id);
        const details = await Promise.all(candidates.map((game) => gamesApi.getGame(game.id).catch(() => null)));
        setPendingInvites(details.filter((game): game is GameDetail => !!game && !!game.players.find((player: Player) => player.id === user.id && !player.has_accepted)));
      } catch {
        // Keep the last good lobby while the connection recovers.
      } finally {
        inFlight = false;
      }
    };
    void syncLobby();
    const timer = window.setInterval(syncLobby, 2000);
    window.addEventListener('focus', syncLobby);
    document.addEventListener('visibilitychange', syncLobby);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', syncLobby);
      document.removeEventListener('visibilitychange', syncLobby);
    };
  }, [id, user]);

  // Keep an open game feeling live. Scores and settlement votes are shared state,
  // so a short foreground poll is the fallback when push delivery is unavailable.
  useEffect(() => {
    if (!id || !selectedGame || selectedGame.status === 'cancelled') return;
    const scoresOnly = selectedGame.status === 'in_progress' && activeTab === 'scoring';
    // The finish → confirm → payout phase is time-sensitive: poll fast so each
    // player's confirmation and the payout landing show up almost immediately.
    const verifying = selectedGame.status === 'completed' && !selectedGame.all_scores_verified;
    const fast = scoresOnly || verifying;
    const sync = () => {
      if (document.visibilityState === 'visible') void refreshGame(scoresOnly);
    };
    const t = window.setInterval(sync, fast ? 1200 : 3500);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      window.clearInterval(t);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGame?.status, selectedGame?.all_scores_verified, id, activeTab]);

  // A join request is a small, time-sensitive payload. Poll it separately while
  // the host has a pending game open so a new golfer appears almost immediately
  // without repeatedly reloading scores, bets, and settlements.
  useEffect(() => {
    if (!id || !user || selectedGame?.created_by !== user.id || selectedGame.status !== 'pending') return;
    let requestInFlight = false;
    const syncRequests = async () => {
      if (document.visibilityState !== 'visible' || requestInFlight) return;
      requestInFlight = true;
      try {
        setJoinRequests(await gamesApi.getJoinRequests(parseInt(id)));
      } catch {
        // Keep the last good list during a transient network error.
      } finally {
        requestInFlight = false;
      }
    };
    void syncRequests();
    const timer = window.setInterval(syncRequests, 1500);
    window.addEventListener('focus', syncRequests);
    document.addEventListener('visibilitychange', syncRequests);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', syncRequests);
      document.removeEventListener('visibilitychange', syncRequests);
    };
  }, [id, selectedGame?.created_by, selectedGame?.status, user]);

  const buzz = () => { if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(10); };

  const loadData = async () => {
    try {
      if (id) {
        // A specific game is open — don't fetch the whole games list.
        const gid = parseInt(id);
        const [gameDetail, gameBets] = await Promise.all([
          gamesApi.getGame(gid),
          betsApi.getGameBets(gid).catch(() => []),
        ]);
        setSelectedGame(gameDetail);
        setBets(gameBets);
        await loadSettlements(gid);
        loadJoinRequests(gid, gameDetail);
        // Opening a game: a live round jumps straight to Score Entry; anything
        // else opens on details. Don't override a tab the user already switched to.
        setActiveTab((t) => (t === 'list' ? (gameDetail.status === 'in_progress' ? 'scoring' : 'details') : t));
        if (gameDetail.status === 'completed' || gameDetail.status === 'disputed') {
          gamesApi.getVerification(gid).then(setVerifications).catch(() => {});
        }
      } else {
        const gamesData = await gamesApi.getMyGames();
        setGames(Array.isArray(gamesData) ? gamesData : []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Reload only the open game's detail + bets + verifications after an action.
  const refreshGame = async (scoresOnly = false) => {
    if (!id) return;
    if (refreshInFlight.current) {
      refreshQueued.current = true;
      return;
    }
    const gid = parseInt(id);
    refreshInFlight.current = true;
    try {
      if (scoresOnly) {
        const [gameDetail, gameBets, gameSettlements] = await Promise.all([
          gamesApi.getGame(gid),
          betsApi.getGameBets(gid).catch(() => []),
          betsApi.getGameSettlements(gid).catch(() => null),
        ]);
        setSelectedGame(gameDetail);
        setBets(gameBets);
        if (gameSettlements) {
          const entries = Object.entries(gameSettlements.settlements).map(([k, v]) => [Number(k), v] as const);
          setSettlements(Object.fromEntries(entries));
        }
        if (gameDetail.status !== 'in_progress') {
          setActiveTab('details');
          refreshQueued.current = true;
        }
        return;
      }
      const [gameDetail, gameBets, gameSettlements] = await Promise.all([
        gamesApi.getGame(gid),
        betsApi.getGameBets(gid).catch(() => []),
        betsApi.getGameSettlements(gid).catch(() => null),
      ]);
      setSelectedGame(gameDetail);
      setBets(gameBets);
      if (gameSettlements) {
        const entries = Object.entries(gameSettlements.settlements).map(([k, v]) => [Number(k), v] as const);
        setSettlements(Object.fromEntries(entries));
      }
      loadJoinRequests(gid, gameDetail);
      if (gameDetail.status === 'completed' || gameDetail.status === 'disputed') {
        gamesApi.getVerification(gid).then(setVerifications).catch(() => {});
      }
    } catch (e) {
      // keep current view on transient error
    } finally {
      refreshInFlight.current = false;
      if (refreshQueued.current) {
        refreshQueued.current = false;
        void refreshGame(false);
      }
    }
  };

  // Side bets pay out by agreement — fetch all proposals for the game in one call.
  const loadSettlements = async (gid: number) => {
    try {
      const r = await betsApi.getGameSettlements(gid);
      const entries = Object.entries(r.settlements).map(([k, v]) => [Number(k), v] as const);
      setSettlements(Object.fromEntries(entries));
    } catch {
      /* keep existing settlements on transient error */
    }
  };

  // Pending join requests are only the host's business, and only while pending.
  const loadJoinRequests = (gid: number, game: GameDetail) => {
    if (user && game.created_by === user.id && game.status === 'pending') {
      gamesApi.getJoinRequests(gid).then(setJoinRequests).catch(() => setJoinRequests([]));
    } else {
      setJoinRequests([]);
    }
  };

  const handleApproveRequest = async (reqId: number) => {
    if (!id) return;
    setRosterBusy(true);
    try { await gamesApi.approveJoinRequest(parseInt(id), reqId); await refreshGame(); }
    catch { /* keep view on transient error */ }
    finally { setRosterBusy(false); }
  };

  const handleDeclineRequest = async (reqId: number) => {
    if (!id) return;
    setRosterBusy(true);
    try { await gamesApi.declineJoinRequest(parseInt(id), reqId); await refreshGame(); }
    catch { /* keep view on transient error */ }
    finally { setRosterBusy(false); }
  };

  const inviteViaWhatsApp = () => {
    if (!selectedGame) return;
    // Use the shareable join link so anyone (even without an account yet) can
    // tap it, sign up if needed, and land straight in this game. Fall back to
    // the game page for older games created before invite codes existed.
    const code = (selectedGame as any).invite_code;
    const url = code
      ? `${window.location.origin}/join/${code}`
      : `${window.location.origin}/game/${selectedGame.id}`;
    const when = formatSAST(selectedGame.scheduled_date, {
      weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
    const text = `⛳ Join my FairPlay round "${selectedGame.name}" at ${selectedGame.course_name} on ${when} SAST.\nTap to join: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleAccept = async () => {
    if (!selectedGame) return;
    setActionMsg('');
    try {
      const result = await gamesApi.acceptInvite(selectedGame.id);
      if (result.stake_errors?.length) {
        setActionMsg(`You're in the game, but the opening stake was not paid: ${result.stake_errors[0].detail}. Add funds in Wallet, then join the bet.`);
      }
      await refreshGame();
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Failed to accept invite');
    }
  };

  // Current displayed value for a hole: optimistic local value wins over server.
  const cardVal = (hole: number): number | undefined =>
    myCard[hole] ?? myScores[hole];

  // Save a hole optimistically: update the UI instantly, save in the background.
  const saveHole = async (hole: number, strokes: number) => {
    if (!selectedGame || strokes < 1 || strokes > 20) return;
    setMyCard((prev) => ({ ...prev, [hole]: strokes }));
    buzz();
    try {
      const result = await gamesApi.submitScore(selectedGame.id, hole, strokes);
      if (result.round_complete) {
        setActionMsg('Every card is complete. Check the final scores and confirm — payouts will run automatically.');
        setActiveTab('details');
      }
      await refreshGame(result.round_complete ? false : true);
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Failed to save score');
    }
  };

  // Quick-pick an exact score, then jump focus to the next hole.
  const handleSetHoleScore = (hole: number, strokes: number) => {
    void saveHole(hole, strokes);
    setFocusHole(hole < holeCount ? hole + 1 : null);
  };

  const searchPlayers = async (q: string) => {
    setPlayerQuery(q);
    if (q.trim().length < 2) {
      setPlayerResults([]);
      return;
    }
    try {
      const existingIds = new Set(selectedGame?.players?.map((p) => p.id));
      const results = await communityApi.searchUsers(q.trim());
      setPlayerResults(results.filter((u) => !existingIds.has(u.id)));
    } catch {
      setPlayerResults([]);
    }
  };

  const handleAddPlayer = async (data: { user_id?: number; email?: string; phone?: string }) => {
    if (!selectedGame) return;
    setActionMsg('');
    setRosterBusy(true);
    try {
      await gamesApi.addPlayer(selectedGame.id, data);
      setPlayerQuery('');
      setPlayerResults([]);
      setContactInput('');
      await refreshGame();
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Could not add player');
    } finally {
      setRosterBusy(false);
    }
  };

  const handleAddByContact = () => {
    const v = contactInput.trim();
    if (!v) return;
    // A digit-heavy string is treated as a mobile number, otherwise an email.
    const digits = v.replace(/\D/g, '');
    if (digits.length >= 6 && !v.includes('@')) handleAddPlayer({ phone: v });
    else handleAddPlayer({ email: v });
  };

  const handleRemovePlayer = async (playerId: number) => {
    if (!selectedGame) return;
    setActionMsg('');
    setRosterBusy(true);
    try {
      await gamesApi.removePlayer(selectedGame.id, playerId);
      await refreshGame();
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Could not remove player');
    } finally {
      setRosterBusy(false);
    }
  };

  const handleDeleteGame = async (gameId: number, fromList = false) => {
    if (!window.confirm('Delete this game? Any un-settled stakes are refunded. This cannot be undone.')) return;
    setActionMsg('');
    try {
      await gamesApi.deleteGame(gameId);
      setGames((prev) => prev.filter((g) => g.id !== gameId));
      if (!fromList) {
        setSelectedGame(null);
        setActiveTab('list');
        navigate('/game');
      }
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Could not delete game');
    }
  };

  const handleJoinBet = async (betId: number) => {
    setActionMsg('');
    setBusyBet(betId);
    try {
      await betsApi.joinBet(betId);
      await refreshGame();
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Could not place your stake');
    } finally {
      setBusyBet(null);
    }
  };

  const handleDeclineBet = async (betId: number) => {
    setActionMsg('');
    setBusyBet(betId);
    try {
      await betsApi.declineBet(betId);
      await refreshGame();
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Could not decline');
    } finally {
      setBusyBet(null);
    }
  };

  const handleDeleteBet = async (bet: Bet) => {
    setActionMsg('');
    setBusyBet(bet.id);
    try {
      await betsApi.deleteBet(bet.id);
      setSwipedBetId(null);
      await refreshGame();
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Could not delete the bet');
    } finally {
      setBusyBet(null);
    }
  };

  const handleNominateWinner = async (bet: Bet, playerId: number) => {
    setActionMsg('');
    setBusyBet(bet.id);
    try {
      const r = await betsApi.proposeSettlement(bet.id, [{ player_id: playerId, amount_cents: bet.total_pool }]);
      if (r.settlement) setSettlements((prev) => ({ ...prev, [bet.id]: r.settlement }));
      await refreshGame();
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Could not nominate winner');
    } finally {
      setBusyBet(null);
    }
  };

  const handleVoteSettlement = async (bet: Bet, settlementId: number, approve: boolean) => {
    setActionMsg('');
    setBusyBet(bet.id);
    try {
      const r = await betsApi.voteSettlement(bet.id, settlementId, approve);
      if (r.settlement) setSettlements((prev) => ({ ...prev, [bet.id]: r.settlement }));
      if (r.settled) setBets((prev) => prev.map((item) => item.id === bet.id ? { ...item, status: 'settled' } : item));
      if (r.settled) setSettleResult({ ...r, bet_type: 'side_bet' });
      await refreshGame();
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Could not record your vote');
    } finally {
      setBusyBet(null);
    }
  };

  const handleFinalizeSettlement = async (bet: Bet, settlementId: number) => {
    setActionMsg('');
    setBusyBet(bet.id);
    try {
      const r = await betsApi.finalizeSettlement(bet.id, settlementId);
      if (r.settled) setSettleResult({ ...r, bet_type: 'side_bet' });
      await refreshGame();
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Could not finalize the settlement');
    } finally {
      setBusyBet(null);
    }
  };

  const handleCompleteGame = async () => {
    if (!selectedGame) return;
    // Guard against an accidental tap — especially with scores still missing.
    const expected = playerCount * holeCount;
    const entered = (selectedGame.scores || []).reduce((a, s) => a + (s.holes_played || 0), 0);
    const msg =
      entered < expected
        ? `Only ${entered} of ${expected} scores are in. Finish the round anyway? (You can reopen it if you tapped this by mistake.)`
        : 'Finish the round? Players will then confirm the scores.';
    if (!window.confirm(msg)) return;
    setActionMsg('');
    try {
      await gamesApi.completeGame(selectedGame.id);
      await refreshGame();
      setActiveTab('details');
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Failed to complete game');
    }
  };

  const handleReopen = async () => {
    if (!selectedGame) return;
    setActionMsg('');
    try {
      await gamesApi.reopenGame(selectedGame.id);
      await refreshGame();
      setActiveTab('scoring');
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Could not reopen the game');
    }
  };

  const handleVerify = async (status: 'approved' | 'disputed', disputeReason?: string) => {
    if (!selectedGame) return;
    setActionMsg('');
    try {
      const result = await gamesApi.verifyResults(selectedGame.id, status, disputeReason);
      if (result.auto_settled?.length) {
        setSettleResult({ auto: true, count: result.auto_settled.length });
      }
      if (result.settlement_errors?.length) {
        setActionMsg(`Scores are confirmed, but a payout needs attention: ${result.settlement_errors[0].detail}`);
      }
      await refreshGame();
    } catch (e: any) {
      setActionMsg(e.response?.data?.detail || 'Failed to submit verification');
    }
  };

  const isPastPending = (game: Pick<Game, 'status' | 'scheduled_date'>) => {
    if (game.status !== 'pending') return false;
    const scheduledTime = parseSAST(game.scheduled_date).getTime();
    return Number.isFinite(scheduledTime) && scheduledTime < Date.now();
  };

  const getStatusColor = (game: Pick<Game, 'status' | 'scheduled_date'>) => {
    if (isPastPending(game)) return 'bg-orange-100 text-orange-700';
    switch (game.status) {
      case 'in_progress': return 'bg-green-100 text-green-700';
      case 'completed': return 'bg-blue-100 text-blue-700';
      case 'disputed': return 'bg-red-100 text-red-700';
      case 'cancelled': return 'bg-gray-200 text-gray-600';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  const getStatusLabel = (game: Pick<Game, 'status' | 'scheduled_date'>) => {
    if (isPastPending(game)) return 'Tee time passed';
    switch (game.status) {
      case 'in_progress': return 'Live';
      case 'completed': return 'Completed';
      case 'disputed': return 'Disputed';
      case 'cancelled': return 'Cancelled';
      default: return 'Upcoming';
    }
  };

  const handleCreateBet = async () => {
    if (!selectedGame) return;
    setBetError('');

    if (betAmount < 50 || betAmount > 5000) {
      setBetError('Bet must be between R50 and R5000');
      return;
    }

    const baseLabel = betType === 'side_bet' ? (sideLabel === 'Custom' ? customSide.trim() : sideLabel) : undefined;
    const label = baseLabel && betDrink ? `${baseLabel} · + ${betDrink} for the loser`.slice(0, 60) : baseLabel;
    if (betType === 'side_bet' && !label) {
      setBetError('Give your side bet a name');
      return;
    }

    try {
      const payload: any = {
        game_id: selectedGame.id,
        bet_type: betType,
        amount_per_player: Math.round(betAmount * 100), // Rands -> cents
      };
      if (betType === 'single_hole') payload.hole_number = betHole;
      if (betType === 'side_bet') {
        payload.label = label;
        if (sideOnHole) payload.hole_number = betHole;
      }

      const newBet = await betsApi.createBet(payload);
      try {
        localStorage.setItem('fp_last_stake', String(betAmount));
        localStorage.setItem('fp_last_bettype', betType);
      } catch { /* ignore storage errors */ }
      // Creator stakes immediately so the pool is never empty.
      try {
        await betsApi.joinBet(newBet.id);
      } catch (e: any) {
        setActionMsg(e.response?.data?.detail || 'Bet created, but staking failed — check your balance');
      }
      await refreshGame();
      setShowBetSetup(false);
      setBetDrink('');
    } catch (error: any) {
      setBetError(error.response?.data?.detail || 'Failed to create bet');
    }
  };

  // Open the inline bet panel (optionally forcing a type, e.g. mid-round).
  const openBetSetup = (forceType?: BetKind, forceHole?: number) => {
    setBetError('');
    if (forceType) setBetType(forceType);
    if (forceHole) setBetHole(forceHole);
    setShowBetSetup(true);
  };

  const handleStartGame = async () => {
    if (!selectedGame) return;
    setActionMsg('');
    try {
      await gamesApi.startGame(selectedGame.id);
      await refreshGame();
      setShowHandicapConfirmation(false);
      setHandicapsConfirmed(false);
      setActiveTab('scoring');
    } catch (error: any) {
      setActionMsg(error.response?.data?.detail || 'Failed to start game');
    }
  };

  const playerName = (pid?: number | null) =>
    selectedGame?.players?.find((p) => p.id === pid)?.name || 'Player';

  const betTitle = (b: Bet) => {
    if (b.bet_type === 'side_bet') return b.hole_number ? `${b.label || 'Side bet'} · Hole ${b.hole_number}` : (b.label || 'Side bet');
    if (b.bet_type === 'single_hole') return `Hole ${b.hole_number} shoot-out`;
    if (b.bet_type === 'per_hole') return 'Per hole (whole round)';
    return 'Winner takes all';
  };

  // One bet row: stake button, auto-settle, or side-bet "who won?" agreement.
  const renderBetRow = (b: Bet) => {
    const uid = user?.id;
    const joined = !!(uid && b.participant_ids?.includes(uid));
    const gs = selectedGame?.status;
    const isSideBet = b.bet_type === 'side_bet';
    const declined = !!(uid && (b as any).declined_ids?.includes(uid));
    const canJoin =
      b.status !== 'settled' &&
      !joined &&
      !declined &&
      !!myPlayer?.has_accepted &&
      (gs === 'pending' || (gs === 'in_progress' && (b.bet_type === 'single_hole' || isSideBet)));
    const settlement = settlements[b.id];
    const proposal = settlement && settlement.status === 'proposed' ? settlement : null;
    const canSettleSide =
      isSideBet && b.status !== 'settled' && joined && (gs === 'in_progress' || gs === 'completed');

    const canDelete = b.status !== 'settled' && (b.created_by === uid || isCreator);
    return (
      <div key={b.id} className="relative overflow-hidden rounded-xl">
        {canDelete && (
          <button
            onClick={() => handleDeleteBet(b)}
            disabled={busyBet === b.id}
            className="absolute inset-y-0 right-0 flex w-24 items-center justify-center bg-red-500 text-sm font-semibold text-white disabled:opacity-60"
            aria-label={`Delete ${betTitle(b)}`}
          >
            {busyBet === b.id ? '…' : 'Delete'}
          </button>
        )}
        <div
          onTouchStart={canDelete ? (e) => { swipeStartX.current = e.touches[0].clientX; } : undefined}
          onTouchEnd={canDelete ? (e) => {
            if (swipeStartX.current == null) return;
            const dx = e.changedTouches[0].clientX - swipeStartX.current;
            if (dx < -40) setSwipedBetId(b.id);
            else if (dx > 40) setSwipedBetId(null);
            swipeStartX.current = null;
          } : undefined}
          style={{ transform: swipedBetId === b.id ? 'translateX(-6rem)' : 'translateX(0)' }}
          className="relative space-y-3 rounded-xl bg-gray-50 p-3 transition-transform duration-200"
        >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-base font-semibold text-gray-900">{betTitle(b)}</p>
            <p className="text-sm text-gray-500">
              R{(b.amount_per_player / 100).toFixed(0)} / player • {b.participant_count} in • pool R{(b.total_pool / 100).toFixed(0)}
              {b.status === 'settled' ? ' • settled ✓' : ''}
            </p>
            {canDelete && (
              <p className="mt-0.5 text-[11px] text-gray-400">← swipe to delete</p>
            )}
          </div>
          {joined && b.status !== 'settled' && (
            <span className="text-xs font-medium text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">You're in</span>
          )}
          {canJoin && (
            <div className="flex flex-col items-end gap-1 shrink-0">
              <button
                disabled={busyBet === b.id}
                onClick={() => handleJoinBet(b.id)}
                className="text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-lg transition disabled:opacity-50"
              >
                {busyBet === b.id ? '…' : `Place R${(b.amount_per_player / 100).toFixed(0)}`}
              </button>
              <button
                disabled={busyBet === b.id}
                onClick={() => handleDeclineBet(b.id)}
                className="text-[11px] font-semibold text-gray-400 hover:text-red-500 transition disabled:opacity-50"
              >
                Too rich for my blood
              </button>
            </div>
          )}
          {declined && b.status !== 'settled' && (
            <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-1 rounded-full shrink-0">Passed</span>
          )}
        </div>

        {/* Side bet: nominate the winner, everyone confirms */}
        {canSettleSide && !proposal && (
          <div className="border-t border-gray-200 pt-3">
            <p className="text-xs font-medium text-gray-600 mb-2">
              Who won {b.label}? Tap to nominate — everyone confirms before it pays out.
            </p>
            <div className="flex flex-wrap gap-2">
              {(b.participant_ids || []).map((pid) => (
                <button
                  key={pid}
                  disabled={busyBet === b.id}
                  onClick={() => handleNominateWinner(b, pid)}
                  className="text-sm px-3 py-1.5 rounded-lg bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                >
                  {playerName(pid)}
                </button>
              ))}
            </div>
          </div>
        )}

        {canSettleSide && proposal && (() => {
          const winnerShare = [...proposal.shares].sort((a, z) => z.amount_cents - a.amount_cents)[0];
          const approvedIds = proposal.approvals.filter((a) => a.approved).map((a) => a.player_id);
          const iApproved = uid ? approvedIds.includes(uid) : false;
          const need = (b.participant_ids || []).length;
          return (
            <div className="border-t border-gray-200 pt-3 space-y-2">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">{playerName(proposal.proposed_by)}</span> says{' '}
                <span className="font-semibold text-emerald-700">{playerName(winnerShare?.player_id)}</span> won {b.label}.
              </p>
              <p className="text-xs text-gray-500">{approvedIds.length}/{need} confirmed</p>
              {!iApproved && (
                <div className="flex gap-2">
                  <button
                    disabled={busyBet === b.id}
                    onClick={() => handleVoteSettlement(b, proposal.id, true)}
                    className="flex-1 bg-emerald-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
                  >
                    Confirm
                  </button>
                  <button
                    disabled={busyBet === b.id}
                    onClick={() => handleVoteSettlement(b, proposal.id, false)}
                    className="flex-1 bg-white border border-red-200 text-red-600 py-2 rounded-lg text-sm font-semibold hover:bg-red-50 disabled:opacity-50"
                  >
                    Dispute
                  </button>
                </div>
              )}
              {iApproved && <p className="text-xs text-emerald-700">You confirmed — waiting for the others.</p>}
              {/* No rejections yet — after the grace window anyone can force it through. */}
              {approvedIds.length < need && !proposal.approvals.some((a) => !a.approved) && (
                <button
                  disabled={busyBet === b.id}
                  onClick={() => handleFinalizeSettlement(b, proposal.id)}
                  className="text-xs text-gray-500 hover:text-gray-700 underline disabled:opacity-50"
                >
                  Nobody responding? Force settle
                </button>
              )}
            </div>
          );
        })()}
        </div>
      </div>
    );
  };

  // The bet-setup form, rendered INLINE on the game screen (no page switch).
  const renderBetForm = () => {
    if (!selectedGame) return null;
    const quickAmounts = [50, 100, 200, 500];
    const suggestedDrink = DRINK_OPTIONS[
      selectedGame.course_name.split('').reduce((sum, character) => sum + character.charCodeAt(0), 0) % DRINK_OPTIONS.length
    ];
    const playedHoles = new Set<number>();
    selectedGame.scores?.forEach((s) => s.scores?.forEach((sc) => playedHoles.add(sc.hole)));

    const HolePicker = () => (
      <div className="flex flex-wrap gap-2">
        {HOLES.map((h) => {
          const played = playedHoles.has(h);
          return (
            <button
              key={h}
              type="button"
              disabled={played}
              onClick={() => setBetHole(h)}
              title={played ? 'Already played' : undefined}
              className={`w-10 h-10 rounded-lg font-bold text-sm ${
                betHole === h
                  ? 'bg-emerald-600 text-white'
                  : played
                  ? 'bg-gray-100 text-gray-300 line-through cursor-not-allowed'
                  : 'bg-white text-gray-700 border border-emerald-200 hover:bg-emerald-100'
              }`}
            >
              {h}
            </button>
          );
        })}
      </div>
    );

    return (
      <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-2xl p-5 border border-emerald-200">
        {/* Bet type */}
        <div className="mb-5">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Bet Type</label>
          <div className="grid grid-cols-2 gap-3">
            {([
              { key: 'winner_takes_all', title: 'Winner Takes All', desc: 'Lowest net total wins', live: false },
              { key: 'per_hole', title: 'Per Hole (round)', desc: 'Pool split across all holes', live: false },
              { key: 'single_hole', title: 'Single Hole', desc: 'Low net on one hole', live: true },
              { key: 'side_bet', title: 'Side Bet 🍺', desc: 'Longest drive, worst shot…', live: true },
            ] as { key: BetKind; title: string; desc: string; live: boolean }[])
              .filter((o) => selectedGame.status !== 'in_progress' || o.live)
              .map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setBetType(o.key)}
                  className={`p-4 rounded-xl text-left transition border ${
                    betType === o.key
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-gray-700 border-emerald-200 hover:bg-emerald-100'
                  }`}
                >
                  <div className="font-bold text-sm">{o.title}</div>
                  <div className={`text-xs ${betType === o.key ? 'text-emerald-50' : 'text-gray-500'}`}>{o.desc}</div>
                </button>
              ))}
          </div>

          {betType === 'single_hole' && (
            <div className="mt-4">
              <label className="block text-xs font-semibold text-gray-600 mb-2">Which hole? (played holes are locked)</label>
              <HolePicker />
            </div>
          )}

          {betType === 'side_bet' && (
            <div className="mt-4">
              <label className="block text-xs font-semibold text-gray-600 mb-2">Pick a side bet — the crew agrees the winner after</label>
              <div className="flex flex-wrap gap-2">
                {[...SIDE_PRESETS, 'Custom ✏️'].map((p) => {
                  const active = p === 'Custom ✏️' ? sideLabel === 'Custom' : sideLabel === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        const label = p === 'Custom ✏️' ? 'Custom' : p;
                        setSideLabel(label);
                        // Longest drive / nearest the pin default to a hole.
                        setSideOnHole(HOLE_SIDE_BETS.has(label));
                      }}
                      className={`px-3 py-2 rounded-lg text-sm font-medium ${
                        active ? 'bg-emerald-600 text-white' : 'bg-white text-gray-700 border border-emerald-200 hover:bg-emerald-100'
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
              {sideLabel === 'Custom' && (
                <input
                  type="text"
                  value={customSide}
                  maxLength={60}
                  onChange={(e) => setCustomSide(e.target.value)}
                  placeholder="Name your bet (e.g. Sandy save, three-putt king)"
                  className="mt-3 w-full px-4 py-3 rounded-xl border border-emerald-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                />
              )}

              {/* Whole round vs one hole - clearer than a checkbox */}
              <div className="mt-4">
                <label className="block text-xs font-semibold text-gray-600 mb-2">When?</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setSideOnHole(false)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                      !sideOnHole ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-emerald-200 hover:bg-emerald-100'
                    }`}
                  >
                    Whole round
                  </button>
                  <button
                    type="button"
                    onClick={() => setSideOnHole(true)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                      sideOnHole ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-emerald-200 hover:bg-emerald-100'
                    }`}
                  >
                    On a hole
                  </button>
                </div>
              </div>
              {sideOnHole && (
                <div className="mt-3">
                  <label className="block text-xs font-semibold text-gray-600 mb-2">Which hole? (played holes are locked)</label>
                  <HolePicker />
                </div>
              )}

              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold text-amber-900">Up the stakes <span className="font-medium text-amber-700">(optional)</span></p>
                    <p className="text-[11px] text-amber-700">Add a drink for the loser · Course pick: {suggestedDrink} at {selectedGame.course_name}</p>
                  </div>
                  {betDrink && (
                    <button type="button" onClick={() => setBetDrink('')} className="text-[11px] font-bold text-amber-800 underline">Remove</button>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {DRINK_OPTIONS.map((drink) => (
                    <button
                      key={drink}
                      type="button"
                      onClick={() => setBetDrink(drink)}
                      className={`rounded-full px-2.5 py-1.5 text-[11px] font-bold transition ${betDrink === drink ? 'bg-amber-500 text-emerald-950' : 'bg-white text-amber-900 ring-1 ring-amber-200'}`}
                    >
                      {drink === suggestedDrink ? `★ ${drink}` : drink}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">Bet Amount Per Player</label>
            <div className="grid grid-cols-4 gap-2">
              {quickAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => { setBetAmount(amount); setCustomBet(''); }}
                  className={`px-2 py-3 rounded-xl font-bold transition ${
                    !customBet && betAmount === amount
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-emerald-100 border border-emerald-200'
                  }`}
                >
                  R{amount}
                </button>
              ))}
            </div>
            <div className="relative mt-3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 font-medium">R</div>
              <input
                type="number"
                min={50}
                max={5000}
                inputMode="numeric"
                value={customBet}
                onChange={(e) => {
                  setCustomBet(e.target.value);
                  const v = parseInt(e.target.value, 10);
                  if (!isNaN(v)) setBetAmount(v);
                }}
                placeholder="Own amount (R50 - R5000)"
                className={`w-full pl-8 pr-4 py-3 rounded-xl border transition ${
                  customBet ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-emerald-200'
                } focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200`}
              />
            </div>
          </div>

          {betError && (
            <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm text-center">{betError}</div>
          )}

          <button
            onClick={handleCreateBet}
            className="w-full bg-emerald-600 text-white py-4 rounded-xl font-semibold hover:bg-emerald-700 transition"
          >
            Create &amp; stake R{betAmount}
          </button>
        </div>
      </div>
    );
  };

  const renderGameList = () => {
    const statusRank = (game: Game) => game.status === 'in_progress' ? 0 : game.status === 'pending' && !isPastPending(game) ? 1 : isPastPending(game) ? 2 : game.status === 'disputed' ? 3 : game.status === 'completed' ? 4 : 5;
    const visibleGames = [...games]
      .filter((game) => {
        const matchesText = `${game.name} ${game.course_name}`.toLowerCase().includes(gameQuery.trim().toLowerCase());
        const matchesStatus = gameFilter === 'all'
          || (gameFilter === 'live' && game.status === 'in_progress')
          || (gameFilter === 'upcoming' && game.status === 'pending' && !isPastPending(game))
          || (gameFilter === 'finished' && ['completed', 'disputed'].includes(game.status));
        return matchesText && matchesStatus;
      })
      .sort((a, b) => {
        const rank = statusRank(a) - statusRank(b);
        if (rank) return rank;
        const aTime = new Date(a.scheduled_date).getTime();
        const bTime = new Date(b.scheduled_date).getTime();
        return a.status === 'pending' ? aTime - bTime : bTime - aTime;
      });
    const liveGame = games.find((game) => game.status === 'in_progress');
    const featuredInvite = pendingInvites[0];

    return (
    <div className="space-y-4">
      <div className="sticky top-0 z-20 -mx-2 px-2 pt-1 pb-3 bg-[#0b3a2d]/95 backdrop-blur-md">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-xl font-bold text-white">My Games</h2>
            <p className="text-xs text-emerald-200">Live rounds stay at the top.</p>
          </div>
          <button
            onClick={() => navigate('/quick-game')}
            className="flex items-center space-x-2 bg-emerald-500 text-white px-3 py-2 rounded-xl hover:bg-emerald-400 transition"
          >
            <Plus className="h-4 w-4" />
            <span className="font-semibold">New</span>
          </button>
        </div>
        {liveGame && (
          <button type="button" onClick={() => navigate(`/game/${liveGame.id}`)} className="mb-2 flex w-full items-center justify-between rounded-2xl bg-emerald-500 px-4 py-3 text-left text-white shadow-lg shadow-emerald-950/20">
            <span className="min-w-0"><span className="block text-[10px] font-black uppercase tracking-[0.15em]">Live now</span><span className="block truncate text-sm font-black">{liveGame.name}</span></span>
            <span className="shrink-0 rounded-xl bg-emerald-950 px-3 py-2 text-xs font-black">Continue scoring →</span>
          </button>
        )}
        {featuredInvite && (
          <div className="mb-2 rounded-2xl border border-amber-200 bg-amber-300 px-4 py-3 text-emerald-950 shadow-lg shadow-emerald-950/20" role="status">
            <div className="flex items-center justify-between gap-3">
              <span className="min-w-0"><span className="block text-[10px] font-black uppercase tracking-[0.15em]">You’ve been invited{pendingInvites.length > 1 ? ` · +${pendingInvites.length - 1} more` : ''}</span><span className="block truncate text-sm font-black">{featuredInvite.name}</span><span className="block truncate text-[11px] font-semibold opacity-70">{featuredInvite.course_name}</span></span>
              <button type="button" onClick={() => navigate(`/game/${featuredInvite.id}`)} className="shrink-0 rounded-xl bg-emerald-950 px-3 py-2 text-xs font-black text-white">
                Review &amp; accept →
              </button>
            </div>
          </div>
        )}
        {actionMsg && <p className="mb-2 rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">{actionMsg}</p>}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            value={gameQuery}
            onChange={(event) => setGameQuery(event.target.value)}
            placeholder="Search games or courses"
            className="w-full rounded-xl bg-white pl-9 pr-3 py-2.5 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          />
        </div>
        <div className="flex gap-2 mt-2 overflow-x-auto pb-0.5">
          {(['all', 'live', 'upcoming', 'finished'] as const).map((filter) => (
            <button
              key={filter}
              type="button"
              onClick={() => setGameFilter(filter)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition ${
                gameFilter === filter ? 'bg-amber-300 text-emerald-950' : 'bg-white/10 text-emerald-100 hover:bg-white/20'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 animate-pulse">
              <div className="flex items-center justify-between mb-3">
                <div className="h-4 w-40 bg-gray-200 rounded" />
                <div className="h-4 w-16 bg-gray-200 rounded-full" />
              </div>
              <div className="h-3 w-28 bg-gray-100 rounded mb-3" />
              <div className="flex space-x-3">
                <div className="h-3 w-20 bg-gray-100 rounded" />
                <div className="h-3 w-16 bg-gray-100 rounded" />
                <div className="h-3 w-20 bg-gray-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : games.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm">
          <EmptyState
            image="/images/empty-games.png"
            title="No games yet"
            subtitle="Create your first game, invite your fourball, and put something on the line."
          >
            <button
              onClick={() => navigate('/quick-game')}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg shadow-emerald-900/20 hover:from-emerald-500 hover:to-green-500 transition"
            >
              <Plus className="h-5 w-5" />
              <span>Create Game</span>
            </button>
          </EmptyState>
        </div>
      ) : visibleGames.length === 0 ? (
        <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
          <p className="font-semibold text-gray-800">No rounds found</p>
          <p className="text-sm text-gray-500 mt-1">Try another search or filter.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visibleGames.map((game) => {
            const own = !!(user && game.created_by === user.id);
            const swiped = swipedGameId === game.id;
            return (
            <div key={game.id} className="relative overflow-hidden rounded-xl">
              {own && (
                <button
                  onClick={() => { handleDeleteGame(game.id, true); setSwipedGameId(null); }}
                  className="absolute inset-y-0 right-0 w-24 bg-red-500 text-white text-sm font-semibold flex items-center justify-center"
                >
                  Delete
                </button>
              )}
            <div
              onClick={() => {
                if (swiped) { setSwipedGameId(null); return; }
                setSelectedGame(game as GameDetail);
                setActiveTab('details');
                navigate(`/game/${game.id}`);
              }}
              onTouchStart={own ? (e) => { swipeStartX.current = e.touches[0].clientX; } : undefined}
              onTouchEnd={own ? (e) => {
                if (swipeStartX.current == null) return;
                const dx = e.changedTouches[0].clientX - swipeStartX.current;
                if (dx < -40) setSwipedGameId(game.id);
                else if (dx > 40) setSwipedGameId(null);
                swipeStartX.current = null;
              } : undefined}
              style={{ transform: swiped ? 'translateX(-6rem)' : 'translateX(0)' }}
              className="relative bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:border-emerald-300 cursor-pointer transition-transform duration-200"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <h3 className="font-semibold text-gray-800 truncate min-w-0">{game.name}</h3>
                <span className={`text-xs px-2 py-1 rounded-full shrink-0 whitespace-nowrap ${getStatusColor(game)}`}>
                  {getStatusLabel(game)}
                </span>
              </div>
              <p className="text-sm text-gray-500 mb-2">{game.course_name}</p>
              <div className="flex items-center space-x-4 text-xs text-gray-400">
                <span className="flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  {formatSAST(game.scheduled_date, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="flex items-center">
                  <Users className="h-3 w-3 mr-1" />
                  {game.player_count}/4 players
                </span>
                <span className="capitalize">{game.scoring_format.replace('_', ' ')}</span>
                <span>{game.holes ?? 18} holes</span>
              </div>
            </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
  };

  const renderGameDetails = () => {
    if (!selectedGame) return null;
    const finalBoard = [...(selectedGame.scores || [])]
      .filter((score) => score.holes_played > 0)
      .sort((a, b) => (a.net_strokes ?? a.total_strokes) - (b.net_strokes ?? b.total_strokes));
    const champion = finalBoard[0];
    const runnerUp = finalBoard[1];
    const championNet = champion ? (champion.net_strokes ?? champion.total_strokes) : 0;
    const winningMargin = runnerUp ? (runnerUp.net_strokes ?? runnerUp.total_strokes) - championNet : 0;
    const championPool = champion
      ? bets.filter((bet) => bet.status === 'settled' && bet.winner_id === champion.player_id)
          .reduce((sum, bet) => sum + bet.total_pool, 0)
      : 0;
    const settledSideBets = bets.filter((bet) => bet.bet_type === 'side_bet' && bet.status === 'settled' && bet.winner_id);
    const sideBetPunchline = (label = '') => {
      const lower = label.toLowerCase();
      if (lower.includes('longest drive')) return 'Sent it miles. The fairway sighting remains unconfirmed.';
      if (lower.includes('nearest')) return 'Closest to the pin. Please allow a brief period of unbearable confidence.';
      if (lower.includes('putt')) return 'Made the long one. We will hear about it until Christmas.';
      if (lower.includes('water')) return 'Found the drink first. Bold strategy, expensive ball.';
      if (lower.includes('worst')) return 'A truly elite disaster. Nobody else came close.';
      if (lower.includes('3-putt')) return 'Collected three-putts like loyalty points.';
      if (lower.includes('beer')) return 'Lost the golf, won responsibility for refreshments.';
      return 'The witnesses have spoken. Appeals may be lodged at the nineteenth hole.';
    };

    return (
      <div className="space-y-3">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setActiveTab('list')}
            className="p-2 text-green-200 hover:text-white hover:bg-white/10 rounded-full transition"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-white">{selectedGame.name}</h2>
            <p className="text-sm text-green-200">{selectedGame.course_name}</p>
          </div>
        </div>

        {/* Live round: jump straight into scoring from the top. */}
        {selectedGame.status === 'in_progress' && activeTab !== 'scoring' && (
          <button
            onClick={() => setActiveTab('scoring')}
            className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition flex items-center justify-center space-x-2 shadow-lg shadow-emerald-900/30"
          >
            <Target className="h-5 w-5" />
            <span>Enter Scores</span>
          </button>
        )}

        {selectedGame.status === 'completed' && selectedGame.all_scores_verified && champion && (
          <div className="overflow-hidden rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 via-white to-emerald-50 shadow-sm">
            <div className="px-5 py-4 text-center">
              <p className="text-xs font-extrabold tracking-[0.2em] text-amber-700 uppercase">Clubhouse announcement</p>
              <p className="mt-1 text-3xl">🏆</p>
              <h3 className="text-xl font-extrabold text-gray-900">{champion.player_name} takes it.</h3>
              <p className="mt-1 text-sm text-gray-600">
                {champion.total_strokes} gross · {championNet} net
                {winningMargin > 0 ? ` · won by ${winningMargin}` : ' · survived a tie-break'}
              </p>
              <p className="mt-3 text-sm font-medium text-emerald-800">
                {championPool > 0
                  ? `R${(championPool / 100).toFixed(0)} collected. Suddenly every putt was “never in doubt”.`
                  : 'Bragging rights secured. The victory speech has already gone on too long.'}
              </p>
              <p className="mt-2 text-xs text-gray-500">Scores confirmed. Score-based bets have already been paid.</p>
            </div>
          </div>
        )}

        {settledSideBets.length > 0 && (
          <div className="rounded-2xl border border-emerald-200 bg-white p-4 shadow-sm">
            <p className="text-xs font-extrabold tracking-[0.16em] text-emerald-700 uppercase">Side-bet honours board</p>
            <div className="mt-3 space-y-3">
              {settledSideBets.map((bet) => (
                <div key={bet.id} className="rounded-xl bg-emerald-50 px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold text-gray-900">🏅 {playerName(bet.winner_id)} wins {bet.label || 'the side bet'}</p>
                      <p className="mt-1 text-xs text-gray-600">{sideBetPunchline(bet.label)}</p>
                    </div>
                    <span className="shrink-0 font-extrabold text-emerald-700">R{(bet.total_pool / 100).toFixed(0)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Game Status Card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className={`text-sm px-3 py-1 rounded-full ${getStatusColor(selectedGame)}`}>
              {getStatusLabel(selectedGame)}
            </span>
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600">
                {formatSAST(selectedGame.scheduled_date, {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })} SAST
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-xl font-bold text-gray-800">{playerCount}/4</p>
              <p className="text-xs text-gray-500">Players</p>
            </div>
            <div>
              <p className="text-xl font-bold text-gray-800">{holeCount}</p>
              <p className="text-xs text-gray-500">Holes · {selectedGame.scoring_format.replace('_', ' ')}</p>
            </div>
            <div>
              <p className="text-xl font-bold text-gray-800">{bets.length}</p>
              <p className="text-xs text-gray-500">Active Bets</p>
            </div>
          </div>
        </div>

        {/* Requests to join (host confirms link joiners) */}
        {isCreator && joinRequests.length > 0 && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-amber-200">
            <div className="flex items-center space-x-2 mb-3">
              <UserPlus className="h-4 w-4 text-amber-600" />
              <h3 className="font-semibold text-gray-800">Requests to join ({joinRequests.length})</h3>
            </div>
            <div className="space-y-2">
              {joinRequests.map((r) => (
                <div key={r.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
                  <div className="flex items-center space-x-3 min-w-0">
                    <Avatar url={(r as any).avatar_url} name={r.user_name} size="w-9 h-9" bg="bg-amber-500" />
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800 truncate">{r.user_name}</p>
                      <p className="text-xs text-gray-500">Handicap Index {formatHandicapIndex(r.handicap)}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 shrink-0">
                    <button
                      onClick={() => handleApproveRequest(r.id)}
                      disabled={rosterBusy || playerCount >= 4}
                      className="px-3 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => handleDeclineRequest(r.id)}
                      disabled={rosterBusy}
                      aria-label={`Decline ${r.user_name}`}
                      className="p-2.5 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition disabled:opacity-40"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {playerCount >= 4 && (
              <p className="text-xs text-amber-700 mt-2">Game is full — remove a player to approve someone new.</p>
            )}
          </div>
        )}

        {/* Players */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">Players</h3>
            {selectedGame.status !== 'completed' && selectedGame.status !== 'cancelled' && (
              <button
                onClick={inviteViaWhatsApp}
                className="flex items-center space-x-1.5 text-sm font-semibold text-white bg-[#25D366] hover:brightness-95 px-3 py-1.5 rounded-lg transition"
              >
                <Share2 className="h-4 w-4" />
                <span>Invite</span>
              </button>
            )}
          </div>
          <div className="space-y-3">
            {selectedGame.players?.map((player) => {
              const canManage = isCreator && selectedGame.status === 'pending';
              const isTheCreator = player.id === selectedGame.created_by;
              const swipeable = canManage && !isTheCreator;
              const swiped = swipedPlayerId === player.id;
              return (
                <div key={player.id} className="relative overflow-hidden rounded-xl">
                  {swipeable && (
                    <button
                      onClick={() => { handleRemovePlayer(player.id); setSwipedPlayerId(null); }}
                      disabled={rosterBusy}
                      className="absolute inset-y-0 right-0 w-24 bg-red-500 text-white text-sm font-semibold flex items-center justify-center disabled:opacity-60"
                    >
                      Remove
                    </button>
                  )}
                  <div
                    onTouchStart={swipeable ? (e) => { swipeStartX.current = e.touches[0].clientX; } : undefined}
                    onTouchEnd={swipeable ? (e) => {
                      if (swipeStartX.current == null) return;
                      const dx = e.changedTouches[0].clientX - swipeStartX.current;
                      if (dx < -40) setSwipedPlayerId(player.id);
                      else if (dx > 40) setSwipedPlayerId(null);
                      swipeStartX.current = null;
                    } : undefined}
                    style={{ transform: swiped ? 'translateX(-6rem)' : 'translateX(0)' }}
                    className="relative flex items-center justify-between p-2.5 bg-gray-50 transition-transform duration-200"
                  >
                    <div className="flex items-center space-x-3">
                      <Avatar url={(player as any).avatar_url} name={player.name} size="w-10 h-10" />
                      <div>
                        <p className="font-medium text-gray-800">{player.name}</p>
                        <p className="text-xs text-gray-500">Handicap Index {formatHandicapIndex(player.handicap)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {player.has_accepted ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Clock className="h-5 w-5 text-amber-500" />
                      )}
                      {swipeable && (
                        <button
                          onClick={() => (swiped ? setSwipedPlayerId(null) : setSwipedPlayerId(player.id))}
                          disabled={rosterBusy}
                          aria-label={`Remove ${player.name}`}
                          className="p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition disabled:opacity-40"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Add / swap players (creator, before start) */}
          {isCreator && selectedGame.status === 'pending' && playerCount < 4 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              {!showAddPlayer ? (
                <button
                  onClick={() => setShowAddPlayer(true)}
                  className="flex items-center space-x-2 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
                >
                  <UserPlus className="h-4 w-4" />
                  <span>Add player</span>
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      autoFocus
                      value={playerQuery}
                      onChange={(e) => searchPlayers(e.target.value)}
                      placeholder="Search golfers by name, email or mobile"
                      className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 text-sm"
                    />
                  </div>

                  {playerResults.length > 0 && (
                    <div className="space-y-1 max-h-56 overflow-y-auto">
                      {playerResults.map((u) => (
                        <button
                          key={u.id}
                          onClick={() => handleAddPlayer({ user_id: u.id })}
                          disabled={rosterBusy}
                          className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-emerald-50 text-left disabled:opacity-40"
                        >
                          <div className="flex items-center space-x-2">
                            <Avatar url={(u as any).avatar_url} name={u.full_name} size="w-8 h-8" text="text-white text-sm font-semibold" />
                            <div>
                              <p className="text-sm font-medium text-gray-800">{u.full_name}</p>
                              <p className="text-[11px] text-gray-500">
                                Handicap Index {formatHandicapIndex(u.handicap)}{u.home_club ? ` · ${u.home_club}` : ''}
                              </p>
                            </div>
                          </div>
                          <Plus className="h-4 w-4 text-emerald-600" />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Add by email / mobile directly */}
                  <div className="flex items-center space-x-2">
                    <input
                      value={contactInput}
                      onChange={(e) => setContactInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddByContact(); }}
                      placeholder="…or add by email / mobile"
                      className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 text-sm"
                    />
                    <button
                      onClick={handleAddByContact}
                      disabled={rosterBusy || !contactInput.trim()}
                      className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40"
                    >
                      Add
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <button onClick={inviteViaWhatsApp} className="text-xs font-medium text-[#128C7E] hover:underline">
                      Not on FairPlay? Invite via WhatsApp
                    </button>
                    <button onClick={() => { setShowAddPlayer(false); setPlayerQuery(''); setPlayerResults([]); }} className="text-xs text-gray-400 hover:text-gray-600">
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bets on this game */}
        {bets.length > 0 && (
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800">Bets</h3>
              {(selectedGame.status === 'pending' || selectedGame.status === 'in_progress') && (
                <button
                  onClick={() => openBetSetup(selectedGame.status === 'in_progress' ? 'single_hole' : undefined)}
                  className="flex items-center space-x-1 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add bet</span>
                </button>
              )}
            </div>
            <div className="space-y-3">{bets.map((b) => renderBetRow(b))}</div>
          </div>
        )}

        {/* Inline bet setup — no page switch */}
        {showBetSetup && (selectedGame.status === 'pending' || selectedGame.status === 'in_progress') && (
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1">
              <h3 className="font-semibold text-white">New bet</h3>
              <button onClick={() => setShowBetSetup(false)} className="text-sm text-green-200 hover:text-white">
                Cancel
              </button>
            </div>
            {renderBetForm()}
          </div>
        )}

        {settleResult && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm text-emerald-800">
            {settleResult.auto
              ? `${settleResult.count} score-based bet${settleResult.count === 1 ? '' : 's'} settled and paid automatically. No chasing required. ✓`
              : <>Bet settled ✓{' '}{settleResult.refunded
              ? 'Stakes were refunded (no eligible winner).'
              : settleResult.bet_type === 'per_hole'
              ? 'Per-hole pots paid out to each hole winner (net scores).'
              : settleResult.bet_type === 'single_hole'
              ? `Hole shoot-out paid out: R${((settleResult.payout || 0) / 100).toFixed(0)} to the low net.`
              : settleResult.bet_type === 'side_bet'
              ? 'Side bet paid out to the agreed winner. 🍺'
              : `Winner-takes-all payout: R${((settleResult.payout || 0) / 100).toFixed(0)} (lowest net score).`}</>}
          </div>
        )}

        {actionMsg && (
          <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm text-center">{actionMsg}</div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Invited player who hasn't accepted yet — the main stake is shown up
              front and taken from their wallet the moment they accept. */}
          {selectedGame.status === 'pending' && myPlayer && !myPlayer.has_accepted && (
            <div className="space-y-1.5">
              <button
                onClick={handleAccept}
                className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition flex items-center justify-center space-x-2"
              >
                <CheckCircle className="h-5 w-5" />
                <span>
                  {mainMatchBet
                    ? `Accept & stake R${(mainMatchBet.amount_per_player / 100).toFixed(0)}`
                    : 'Accept Invitation'}
                </span>
              </button>
              <p className="text-xs text-center text-brand-100/70">
                {mainMatchBet
                  ? `The R${(mainMatchBet.amount_per_player / 100).toFixed(0)} match stake comes off your wallet when you accept.`
                  : 'No match stake set yet.'}
                {openSideBets.length > 0 && ` You can add ${openSideBets.length} side bet${openSideBets.length > 1 ? 's' : ''} after.`}
              </p>
            </div>
          )}

          {selectedGame.status === 'pending' && (
            <>
              <button
                onClick={() => openBetSetup()}
                className={`w-full py-3 rounded-xl font-semibold transition flex items-center justify-center space-x-2 ${hasMatchedBet ? 'bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50' : 'bg-amber-300 text-emerald-950 hover:bg-amber-200'}`}
              >
                <DollarSign className="h-5 w-5" />
                <span>{hasMatchedBet ? '+ Add another bet' : bets.length ? '1. Invite someone to take the bet' : '1. Set up the first bet'}</span>
              </button>
              {!hasMatchedBet && (
                <p className="rounded-xl bg-amber-50 px-3 py-2 text-center text-xs font-bold text-amber-800">{bets.length ? 'Another player must accept and stake a bet before tee-off.' : 'Set up a bet before the round can start.'}</p>
              )}
              {/* Creator can start once at least 2 players are in and a bet is funded. */}
              {isCreator && playerCount >= 2 && hasMatchedBet && (
                <button
                  onClick={() => {
                    setHandicapsConfirmed(false);
                    setShowHandicapConfirmation(true);
                  }}
                  className={`w-full bg-green-600 text-white py-3 rounded-xl font-semibold hover:bg-green-700 transition flex items-center justify-center space-x-2 ${
                    acceptedCount === playerCount ? 'animate-pulse shadow-lg shadow-green-600/40' : ''
                  }`}
                >
                  <Play className="h-5 w-5" />
                  <span>{acceptedCount === playerCount ? "2. Everyone's in — Confirm & start" : '2. Confirm & start game'}</span>
                </button>
              )}
              {isCreator && playerCount < 2 && (
                <p className="text-xs text-center text-green-200">Invite at least one more player to start.</p>
              )}
            </>
          )}

          {selectedGame.status === 'in_progress' && (
            <>
              <button
                onClick={() => setActiveTab('scoring')}
                className="w-full bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition flex items-center justify-center space-x-2"
              >
                <Target className="h-5 w-5" />
                <span>Enter Scores</span>
              </button>
              <button
                onClick={() => openBetSetup('single_hole')}
                className="w-full bg-white border border-emerald-200 text-emerald-700 py-3 rounded-xl font-semibold hover:bg-emerald-50 transition flex items-center justify-center space-x-2"
              >
                <DollarSign className="h-5 w-5" />
                <span>Add hole / side bet</span>
              </button>
              {isCreator && (
                <button
                  onClick={handleCompleteGame}
                  className="w-full bg-gray-900 text-white py-3 rounded-xl font-semibold hover:bg-gray-800 transition flex items-center justify-center space-x-2"
                >
                  <CheckCircle className="h-5 w-5" />
                  <span>Finish Round</span>
                </button>
              )}
            </>
          )}

          {/* Verification: after finishing, every player confirms scores. Also
              shown for disputed games so nobody is stranded. */}
          {(selectedGame.status === 'completed' || selectedGame.status === 'disputed') &&
            !selectedGame.all_scores_verified && (() => {
              const isDisputed = selectedGame.status === 'disputed' ||
                verifications.some((v) => v.status === 'disputed');
              const mine = verifications.find((v) => v.player_id === user?.id);
              const iApproved = mine?.status === 'approved';
              const approvedN = verifications.filter((v) => v.status === 'approved').length;
              const reasons = verifications.filter((v) => v.status === 'disputed' && v.dispute_reason);
              return (
                <div className={`rounded-2xl p-4 space-y-3 border ${isDisputed ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                  <p className={`text-sm font-medium ${isDisputed ? 'text-red-800' : 'text-amber-800'}`}>
                    {isDisputed
                      ? 'Scores are disputed. The creator can reopen the round to fix them, then everyone re-confirms.'
                      : 'Round finished — all players must confirm the scores before bets can pay out.'}
                  </p>
                  {reasons.map((r, i) => (
                    <p key={i} className="text-xs text-red-700 italic">“{r.dispute_reason}”</p>
                  ))}
                  {!iApproved ? (
                    <div className="flex space-x-3">
                      <button
                        onClick={() => handleVerify('approved')}
                        className="flex-1 bg-emerald-600 text-white py-3 rounded-xl font-semibold hover:bg-emerald-700 transition"
                      >
                        Scores are correct
                      </button>
                      <button
                        onClick={() => {
                          const why = window.prompt("What's wrong with the scores?") || undefined;
                          handleVerify('disputed', why);
                        }}
                        className="flex-1 bg-white border border-red-200 text-red-600 py-3 rounded-xl font-semibold hover:bg-red-50 transition"
                      >
                        Dispute
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-emerald-700 font-medium">You approved — waiting for the others.</p>
                  )}
                  <p className={`text-xs font-semibold ${isDisputed ? 'text-red-700' : 'text-amber-700'}`}>
                    {approvedN} of {playerCount} players confirmed{approvedN < playerCount ? ' — waiting on the rest…' : ' ✓'}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedGame.players?.map((p) => {
                      const v = verifications.find((x) => x.player_id === p.id);
                      const st = v?.status === 'approved' ? 'ok' : v?.status === 'disputed' ? 'no' : 'wait';
                      return (
                        <span key={p.id} className={`text-[11px] px-2 py-0.5 rounded-full ${st === 'ok' ? 'bg-emerald-100 text-emerald-700' : st === 'no' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                          {st === 'ok' ? '✓' : st === 'no' ? '✕' : '⏳'} {p.name.split(' ')[0]}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

          {/* Reopen: creator can drop back to scoring until a bet has paid out */}
          {isCreator &&
            (selectedGame.status === 'completed' || selectedGame.status === 'disputed') &&
            !bets.some((b) => b.status === 'settled') && (
              <button
                onClick={handleReopen}
                className="w-full bg-white border border-amber-300 text-amber-700 py-3 rounded-xl font-semibold hover:bg-amber-50 transition flex items-center justify-center space-x-2"
              >
                <ArrowLeft className="h-5 w-5" />
                <span>Reopen scoring</span>
              </button>
            )}

          {selectedGame.status === 'completed' && selectedGame.all_scores_verified && (
            <p className="text-xs text-center text-green-200">Scores final. Score-based payouts are complete.</p>
          )}

          {/* Creator can delete the game (un-settled stakes are refunded) */}
          {isCreator && (
            <button
              onClick={() => handleDeleteGame(selectedGame.id)}
              className="w-full mt-2 text-sm font-medium text-red-300 hover:text-red-200 flex items-center justify-center space-x-2 py-2"
            >
              <X className="h-4 w-4" />
              <span>Delete game</span>
            </button>
          )}
        </div>
      </div>
  );};

  const renderBetting = () => {
    if (!selectedGame) return null;
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setActiveTab('details')}
            className="p-2 text-green-200 hover:text-white hover:bg-white/10 rounded-full transition"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-white">Set Up Bet</h2>
            <p className="text-sm text-green-200">{selectedGame.name}</p>
          </div>
        </div>

        {renderBetForm()}
      </div>
    );
  };

  const renderScoring = () => {
    if (!selectedGame) return null;
    const currentScoreHole = focusHole ?? HOLES.find((h) => cardVal(h) === undefined) ?? holeCount;

    return (
      <div className="h-[calc(100dvh-10.5rem)] space-y-3 overflow-hidden">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center space-x-3 min-w-0">
            <button
              onClick={() => setActiveTab('details')}
              className="p-2 text-green-200 hover:text-white hover:bg-white/10 rounded-full transition"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-white">Score Entry</h2>
              <p className="text-sm text-green-200 truncate">{selectedGame.name}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => showBetSetup ? setShowBetSetup(false) : openBetSetup('side_bet', currentScoreHole)}
            className="shrink-0 rounded-xl border border-amber-300/70 bg-amber-300 px-3 py-2 text-sm font-bold text-emerald-950 shadow-sm hover:bg-amber-200 active:scale-95 transition"
          >
            {showBetSetup ? 'Close bet' : '+ Side bet'}
          </button>
        </div>

        {actionMsg && (
          <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm text-center">{actionMsg}</div>
        )}

        {showBetSetup && (
          <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 p-3 sm:items-center" role="presentation">
          <section className="max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-3xl border border-amber-200 bg-white p-4 shadow-2xl" aria-label="Create a side bet" role="dialog" aria-modal="true">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-amber-700">Quick side bet</p>
                <h3 className="text-lg font-bold text-gray-900">Keep the game spicy</h3>
                <p className="text-xs text-gray-500">Set it up here, then carry on scoring.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowBetSetup(false)}
                className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close side bet form"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {renderBetForm()}
          </section>
          </div>
        )}

        {(() => {
          const liveBets = bets.filter((b) => b.status !== 'settled').sort((a, b) => b.id - a.id);
          const joinable = liveBets.filter((b) => {
            const joined = !!(user?.id && b.participant_ids?.includes(user.id));
            const declined = !!(user?.id && (b as any).declined_ids?.includes(user.id));
            return !joined && !declined && !!myPlayer?.has_accepted && (b.bet_type === 'single_hole' || b.bet_type === 'side_bet');
          });
          const featured = joinable[0] || liveBets[0];
          if (!featured) return null;
          const inPlay = liveBets.reduce((sum, b) => sum + b.total_pool, 0);
          return (
            <button type="button" onClick={() => setFeaturedBetId(featured.id)}
              className={`flex min-h-[66px] w-full items-center gap-3 rounded-2xl px-3 py-2 text-left shadow-sm ${joinable.length ? 'bg-gradient-to-r from-amber-500 to-amber-400 text-emerald-950' : 'bg-gradient-to-r from-emerald-600 to-green-600 text-white'}`}
              aria-label={`View bet details for ${betTitle(featured)}`}>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-black uppercase tracking-wider opacity-80">
                  {joinable.length > 1 ? `${joinable.length} bets awaiting you` : joinable.length ? 'New bet — you’re invited' : `R${(inPlay / 100).toFixed(0)} in play`}
                </p>
                <p className="line-clamp-2 text-base font-extrabold leading-5">{betTitle(featured)}</p>
                <p className="text-xs font-semibold opacity-85">R{(featured.amount_per_player / 100).toFixed(0)} each · {featured.participant_count} in</p>
              </div>
              <span className={`shrink-0 rounded-xl px-3 py-2 text-sm font-black ${joinable.length ? 'bg-emerald-950 text-white' : 'bg-white/15 text-white'}`}>View bet</span>
            </button>
          );
        })()}

        {/* Current hole - big, tap-first, auto-advances */}
        {(() => {
          const firstUnscored = HOLES.find((h) => cardVal(h) === undefined) ?? null;
          const activeHole = focusHole ?? firstUnscored;
          if (!activeHole) {
            return (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center">
                <p className="text-2xl font-bold text-gray-800 mb-1">Round complete 🎉</p>
                <p className="text-sm text-gray-500">All {holeCount} holes logged. Tap Finish Round when everyone's done.</p>
              </div>
            );
          }
          const saved = cardVal(activeHole);
          const savedDifference = saved === undefined ? null : saved - parForHole(activeHole);
          const holeScores = (selectedGame.scores || []).map((line) => ({
            playerId: line.player_id,
            playerName: line.player_name,
            strokes: line.scores?.find((score) => score.hole === activeHole)?.strokes,
          }));
          return (
            <div className="rounded-2xl border border-emerald-100 bg-white p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <button
                  onClick={() => setFocusHole(activeHole > 1 ? activeHole - 1 : 1)}
                  disabled={activeHole <= 1}
                  className="p-2 text-gray-400 hover:text-emerald-600 disabled:opacity-30"
                  aria-label="Previous hole"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="text-center">
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-emerald-600">Now playing</p>
                  <p className="text-xl font-extrabold text-gray-900">Enter score · Hole {activeHole}</p>
                  <p className="text-sm text-gray-500">
                    Par {parForHole(activeHole)}
                    {savedDifference !== null && savedDifference <= 2 && (
                      <span className={`ml-2 font-semibold ${diffClass(savedDifference)}`}>
                        {diffLabel(savedDifference, activeHole, saved)}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  onClick={() => setFocusHole(activeHole < holeCount ? activeHole + 1 : holeCount)}
                  disabled={activeHole >= holeCount}
                  className="p-2 text-gray-400 hover:text-emerald-600 disabled:opacity-30 rotate-180"
                  aria-label="Next hole"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              </div>

              {/* Quick pick: the label makes the result clear before the tap. */}
              <div className="mb-2 grid grid-cols-4 gap-1.5">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                  <button
                    key={n}
                    onClick={() => handleSetHoleScore(activeHole, n)}
                    aria-label={`${n} strokes, ${scoreLabel(n, parForHole(activeHole), activeHole)}`}
                    className={`min-h-[54px] rounded-xl px-1 py-1.5 transition active:scale-95 ${
                      saved === n
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-emerald-100'
                    }`}
                  >
                    <span className="block text-lg font-extrabold leading-5">{n}</span>
                    <span className={`block line-clamp-2 text-[11px] font-bold leading-3 ${saved === n ? 'text-emerald-50' : 'text-gray-600'}`}>
                      {scoreLabel(n, parForHole(activeHole), activeHole).split(' — ')[0]}
                    </span>
                  </button>
                ))}
              </div>

              {savedDifference !== null && savedDifference > 2 && (
                <p className="mb-2 rounded-xl bg-amber-50 px-3 py-2 text-center text-xs font-bold leading-4 text-amber-800">
                  {diffLabel(savedDifference, activeHole, saved)}
                </p>
              )}

              <p className="text-center text-xs text-gray-500">Tap another number at any time to correct the score.</p>

              <div className="mt-2 border-t border-gray-100 pt-2">
                <div className="mb-1 flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Fourball · hole {activeHole}</p>
                  <span className="text-[11px] text-emerald-600">Live</span>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {holeScores.map((score) => (
                    <div
                      key={score.playerId}
                      className={`min-w-0 rounded-xl px-2 py-1.5 text-center ${
                        score.playerId === user?.id ? 'bg-emerald-50 ring-1 ring-emerald-200' : 'bg-gray-50'
                      }`}
                    >
                      <span className="block truncate text-xs font-bold text-gray-600">
                        {score.playerId === user?.id ? 'You' : score.playerName.split(' ')[0]}
                      </span>
                      <span className={`block text-base font-extrabold leading-5 ${score.strokes == null ? 'text-gray-300' : 'text-gray-900'}`}>
                        {score.strokes ?? '–'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        <div className={`grid gap-2 ${isCreator ? 'grid-cols-4' : 'grid-cols-3'}`}>
          <button type="button" onClick={() => setScoringSheet('bets')} className="rounded-xl bg-white/10 px-2 py-2 text-xs font-bold text-white ring-1 ring-white/15">
            Bets · {bets.filter((bet) => bet.status !== 'settled').length}
          </button>
          <button type="button" onClick={() => setScoringSheet('card')} className="rounded-xl bg-white/10 px-2 py-2 text-xs font-bold text-white ring-1 ring-white/15">
            Card · {HOLES.reduce((total, hole) => total + (cardVal(hole) ?? 0), 0)}
          </button>
          <button type="button" onClick={() => setScoringSheet('leaderboard')} className="rounded-xl bg-white/10 px-2 py-2 text-xs font-bold text-white ring-1 ring-white/15">
            Leaderboard
          </button>
          {isCreator && (
            <button type="button" onClick={handleCompleteGame} className="rounded-xl bg-amber-300 px-2 py-2 text-xs font-black text-emerald-950">
              Finish
            </button>
          )}
        </div>

        {featuredBetId !== null && (() => {
          const bet = bets.find((item) => item.id === featuredBetId && item.status !== 'settled');
          if (!bet) return null;
          const joined = !!(user?.id && bet.participant_ids?.includes(user.id));
          const declined = !!(user?.id && bet.declined_ids?.includes(user.id));
          const canJoin = !joined && !declined && !!myPlayer?.has_accepted && ['side_bet', 'single_hole'].includes(bet.bet_type);
          const [purpose, drink] = (bet.label || '').split(' · + ');
          const betFor = bet.bet_type === 'side_bet' ? (purpose || 'Side bet') : betTitle(bet);
          return (
            <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 p-3 sm:items-center" role="presentation">
              <section className="max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-5 shadow-2xl" role="dialog" aria-modal="true" aria-label={`Bet details: ${betFor}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-wider text-emerald-700">What you're betting on</p>
                    <h3 className="mt-1 text-2xl font-black leading-tight text-gray-900">{betFor}</h3>
                  </div>
                  <button type="button" onClick={() => setFeaturedBetId(null)} className="rounded-full bg-gray-100 p-2 text-gray-600" aria-label="Close bet details">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-600">{bet.hole_number ? `Hole ${bet.hole_number}` : 'Whole round'} · {selectedGame.course_name}</p>
                {drink && <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900">Plus {drink}</p>}
                <div className="mt-4 grid grid-cols-3 gap-2 rounded-2xl bg-gray-50 p-3 text-center">
                  <div><p className="text-xs font-semibold text-gray-500">Your stake</p><p className="text-lg font-black text-gray-900">R{(bet.amount_per_player / 100).toFixed(0)}</p></div>
                  <div><p className="text-xs font-semibold text-gray-500">Players in</p><p className="text-lg font-black text-gray-900">{bet.participant_count}</p></div>
                  <div><p className="text-xs font-semibold text-gray-500">Pool now</p><p className="text-lg font-black text-gray-900">R{(bet.total_pool / 100).toFixed(0)}</p></div>
                </div>
                {bet.participant_ids?.length ? <p className="mt-3 text-sm text-gray-600">In so far: {bet.participant_ids.map((id) => playerName(id)).join(', ')}</p> : null}
                {canJoin ? (
                  <button type="button" disabled={busyBet === bet.id} onClick={() => handleJoinBet(bet.id)} className="mt-5 w-full rounded-xl bg-emerald-700 py-3 text-base font-black text-white disabled:opacity-50">
                    {busyBet === bet.id ? 'Joining…' : `Place R${(bet.amount_per_player / 100).toFixed(0)} bet`}
                  </button>
                ) : (
                  <p className="mt-4 text-center text-sm font-semibold text-emerald-700">{joined ? "You're in this bet" : declined ? 'You passed on this bet' : 'This bet is already in play'}</p>
                )}
                <button type="button" onClick={() => { setFeaturedBetId(null); setScoringSheet('bets'); }} className="mt-3 w-full py-2 text-sm font-bold text-emerald-800 underline">
                  View all {bets.filter((item) => item.status !== 'settled').length} live bets
                </button>
              </section>
            </div>
          );
        })()}

        {scoringSheet && (
          <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 p-3 sm:items-center" role="presentation">
            <section className="max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-4 shadow-2xl" role="dialog" aria-modal="true" aria-label={`${scoringSheet} panel`}>
              <div className="sticky top-0 z-10 mb-3 flex items-center justify-between border-b border-gray-100 bg-white pb-3">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.14em] text-emerald-600">Stay in Score Entry</p>
                  <h3 className="text-xl font-black capitalize text-gray-900">{scoringSheet}</h3>
                </div>
                <button type="button" onClick={() => setScoringSheet(null)} className="rounded-full bg-gray-100 p-2 text-gray-600" aria-label="Close panel">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {scoringSheet === 'bets' && (
                <div className="space-y-3">
                  {bets.filter((bet) => bet.status !== 'settled').length ? (
                    [...bets].filter((bet) => bet.status !== 'settled').sort((a, b) => b.id - a.id).map((bet) => renderBetRow(bet))
                  ) : (
                    <div className="py-8 text-center">
                      <p className="font-bold text-gray-800">No live bets yet</p>
                      <p className="mt-1 text-sm text-gray-500">Start one without leaving the scorecard.</p>
                    </div>
                  )}
                  <button type="button" onClick={() => { setScoringSheet(null); openBetSetup('side_bet', currentScoreHole); }} className="w-full rounded-xl bg-amber-300 py-3 text-sm font-black text-emerald-950">
                    + Create a side bet
                  </button>
                </div>
              )}

              {scoringSheet === 'card' && (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {HOLES.map((hole) => {
                    const saved = cardVal(hole);
                    return (
                      <button key={hole} type="button" onClick={() => { setFocusHole(hole); setScoringSheet(null); }} className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left ${saved !== undefined ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200'}`}>
                        <span className="text-sm font-bold text-gray-600">Hole {hole} · Par {parForHole(hole)}</span>
                        <span className={`text-lg font-black ${saved !== undefined ? diffClass(saved - parForHole(hole)) : 'text-gray-300'}`}>{saved ?? '–'}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {scoringSheet === 'leaderboard' && (
                <div className="space-y-2">
                  {(() => {
                    const ranked = [...(selectedGame.scores || [])].filter((score) => (score.holes_played || 0) > 0).sort((a, b) => (a.net_strokes ?? a.total_strokes ?? 0) - (b.net_strokes ?? b.total_strokes ?? 0));
                    const leaderNet = ranked.length ? (ranked[0].net_strokes ?? ranked[0].total_strokes ?? 0) : 0;
                    if (!ranked.length) return <p className="py-8 text-center text-sm text-gray-500">No scores yet — be the first on the board.</p>;
                    return ranked.map((score, index) => {
                      const net = score.net_strokes ?? score.total_strokes ?? 0;
                      const isMe = score.player_id === user?.id;
                      return (
                        <div key={score.player_id} className={`flex items-center justify-between rounded-xl p-3 ${isMe ? 'bg-emerald-50 ring-1 ring-emerald-200' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-3">
                            <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-black ${index === 0 ? 'bg-amber-400 text-emerald-950' : 'bg-gray-200 text-gray-600'}`}>{index + 1}</span>
                            <div><p className="font-bold text-gray-800">{score.player_name}{isMe ? ' (you)' : ''}</p><p className="text-[11px] text-gray-400">Index {formatHandicapIndex(score.handicap, '0.0')} · thru {score.holes_played}</p></div>
                          </div>
                          <div className="text-right"><p className="font-black text-gray-800">{net} net</p><p className="text-[11px] text-gray-400">{index === 0 ? 'leader' : `+${net - leaderNet}`}</p></div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'list':
        return renderGameList();
      case 'details':
        return renderGameDetails();
      case 'betting':
        return renderBetting();
      case 'scoring':
        return renderScoring();
      default:
        return renderGameList();
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto pb-20">
        {renderContent()}
      </div>

      {showHandicapConfirmation && selectedGame && (() => {
        const missingIndex = selectedGame.players.some((player) => typeof player.handicap !== 'number');
        return (
          <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 p-4 sm:items-center" role="presentation">
            <section
              role="dialog"
              aria-modal="true"
              aria-labelledby="handicap-confirmation-title"
              className="w-full max-w-md rounded-3xl border border-accent-400/40 bg-brand-950 p-5 text-white shadow-2xl"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="club-eyebrow">BEFORE THE FIRST TEE</p>
                  <h2 id="handicap-confirmation-title" className="mt-1 text-2xl font-black">Check every Handicap Index</h2>
                  <p className="mt-2 text-sm leading-relaxed text-brand-100/80">
                    These values decide net scores and payouts. Ask the fourball to confirm them before the game starts.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowHandicapConfirmation(false)}
                  aria-label="Close Handicap Index confirmation"
                  className="rounded-full p-2 text-brand-100 hover:bg-white/10"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mt-5 space-y-2">
                {selectedGame.players.map((player) => (
                  <div key={player.id} className="flex items-center justify-between rounded-2xl bg-white/[0.08] px-4 py-3">
                    <div>
                      <p className="font-bold">{player.name}</p>
                      <p className="text-xs text-brand-100/65">{player.has_accepted ? 'Accepted' : 'Still to accept'}</p>
                    </div>
                    <span className={`text-lg font-black ${typeof player.handicap === 'number' ? 'text-accent-300' : 'text-red-300'}`}>
                      {typeof player.handicap === 'number' ? formatHandicapIndex(player.handicap) : 'Not set'}
                    </span>
                  </div>
                ))}
              </div>

              {missingIndex ? (
                <p role="alert" className="mt-4 rounded-xl border border-red-400/30 bg-red-400/10 px-3 py-2 text-sm text-red-200">
                  Every player must add a Handicap Index to their profile before this game can start.
                </p>
              ) : (
                <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-3 text-sm text-brand-100">
                  <input
                    type="checkbox"
                    checked={handicapsConfirmed}
                    onChange={(event) => setHandicapsConfirmed(event.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-amber-300"
                  />
                  <span>I have checked these Handicap Index values with the players.</span>
                </label>
              )}

              <button
                type="button"
                disabled={missingIndex || !handicapsConfirmed}
                onClick={handleStartGame}
                className="mt-4 w-full rounded-xl bg-accent-300 px-4 py-3 font-black text-brand-950 transition hover:bg-accent-200 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Confirm and start game
              </button>
            </section>
          </div>
        );
      })()}
    </Layout>
  );
};

export default GameHub;
